import { Component, ComponentInterface, Element, h, Host, Prop, Watch } from '@stencil/core';

interface UiCmsHeadlineBody {
  headline: string;
  isHeadlineCentered?: boolean;
  overline?: string;
  leadtext?: string;
  iconPath?: string;
  headingLevel?: number | string;
  headingStyle?: number;
}

const headingSizes = { 1: '4xl', 2: '3xl', 3: 'xl', 4: 'lg', 5: 'base' };

@Component({
  tag: 'ui-cms-headline',
  styleUrl: 'ui-cms-headline.scss',
})
export class UiCmsHeadline implements ComponentInterface {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsHeadlineBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() inverted: boolean;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  private headingElement: HTMLElement;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  // Magnolia adds <p> elements when there are line breaks or other formatting spaces in the text.
  // This function removes those <p> elements.
  private stripParagraphTags(html: string): string {
    return html.replace(/<p>/g, '').replace(/<\/p>/g, '<br />');
  }

  private applyCustomFontWeightIfSet() {
    const styles = getComputedStyle(document.documentElement);
    const customFontWeights: Record<number, string> = {
      1: styles.getPropertyValue('--component-headline-font-weight-level-1'),
      2: styles.getPropertyValue('--component-headline-font-weight-level-2'),
      3: styles.getPropertyValue('--component-headline-font-weight-level-3'),
      4: styles.getPropertyValue('--component-headline-font-weight-level-4'),
    };

    Object.entries(customFontWeights).forEach(([level, weight]) => {
      if (weight && parseInt(level, 10) === parseInt(`${this.body.headingLevel}`, 10)) {
        this.headingElement.style.fontWeight = weight;
      }
    });
  }

  componentDidLoad() {
    this.applyCustomFontWeightIfSet();
  }

  render() {
    const { overline, headline, iconPath, isHeadlineCentered, leadtext, headingLevel, headingStyle } = this.body;
    const level = headingLevel || 1;
    const HeadingTag = `h${level}`;
    const headingSize = `sd-headline--size-${headingSizes[headingStyle || level]}`;

    return (
      <Host class={{ centered: isHeadlineCentered }}>
        {overline && <p class={{ overline: true, 'overline--inverted': this.inverted }} innerHTML={overline}></p>}
        <HeadingTag
          ref={(el) => {
            this.headingElement = el;
          }}
          class={{
            [`heading sd-headline sd-headline--inline ${headingSize}`]: true,
            'sd-headline--inverted': this.inverted,
          }}
        >
          {iconPath && <sd-icon library={window.ui.cmsState.iconLibrary} name={iconPath}></sd-icon>}
          {headline && <span innerHTML={this.stripParagraphTags(headline)}></span>}
        </HeadingTag>
        {leadtext && (
          <p
            class={{
              'sd-leadtext': true,
              leadtext: true,
              'sd-leadtext--inverted': this.inverted,
            }}
            innerHTML={leadtext}
          ></p>
        )}
      </Host>
    );
  }
}
